import { useState } from 'react'
import { useSearchParams, Form, useSubmit } from 'react-router'
import { formatMoney, getCurrentYear } from '@trinity/utils'
import {
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Dialog,
  TrendingDown,
  TrendingUp,
  TrendingFlat,
} from '../../components'
import { TrendCard } from './TrendCard'
import { Leaderboard } from './Leaderboard'
import { Breakdown } from './BreakdownContainer'

interface DealerRankProps {
  dealerStats: TrinityAPI.DealerRankType
  leaderboard: TrinityAPI.DealerLeaderboardType[]
  collections: TrinityAPI.DealerCollectionRankType[]
  garmentTypes: TrinityAPI.DealerGarmentTypesRankType[]
  historyRanks: TrinityAPI.DealerHistoryRankType
}

export function DealerRank({ dealerStats, leaderboard, collections, garmentTypes, historyRanks }: DealerRankProps) {
  const submit = useSubmit()
  const [historyOpen, setHistoryOpen] = useState(false)
  const currentYear = getCurrentYear()

  // We only want the last 5 years of history, including the current year
  const filteredRanks = Object.values(historyRanks)
    .filter(({ year }) => year >= currentYear - 4)
    .sort((a, b) => b.year - a.year)

  return (
    <Stack spacing={8} component={Form} method='GET' onChange={e => submit(e.currentTarget)}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
        <Stack spacing={1}>
          <Typography variant='h4'>Dealer Rank</Typography>
          <Typography variant='body2'>Here's How You Stack Up Against Other Clothiers</Typography>
        </Stack>
        {filteredRanks.length > 0 && <Button onClick={() => setHistoryOpen(true)}>Annual Dealer Rank</Button>}
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
        <TrendCard amount={dealerStats.revenue} trend={dealerStats.revenueTrend} />
        <TrendCard units amount={dealerStats.units} trend={dealerStats.unitsTrend} />
      </Stack>
      <TimeframeToggle />
      <Leaderboard leaderboard={leaderboard} />
      <Breakdown isCollections data={collections} />
      <Breakdown isGarments data={garmentTypes} />

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth='sm' fullWidth>
        <Stack spacing={2} sx={{ p: 3 }}>
          <Typography variant='h5'>Your Ranking History</Typography>
          <Stack spacing={3}>
            {filteredRanks.map(history => (
              <RankRow key={history.year} history={history} historyRanks={historyRanks} />
            ))}
          </Stack>
        </Stack>
      </Dialog>
    </Stack>
  )
}

function TimeframeToggle() {
  const [params] = useSearchParams()
  const [timeframe, setTimeframe] = useState(params.get('timeframe') ?? 'mtd')

  return (
    <>
      <input type='hidden' name='timeframe' value={timeframe} />
      <ToggleButtonGroup
        size='large'
        value={timeframe}
        onChange={(_e, v) => v && setTimeframe(v)}
        sx={{ alignSelf: 'center', width: 'fit-content' }}
      >
        <ToggleButton type='submit' value='mtd'>
          Month To Date
        </ToggleButton>
        <ToggleButton type='submit' value='ytd'>
          Year To Date
        </ToggleButton>
      </ToggleButtonGroup>
    </>
  )
}

interface RankRowProps {
  history: TrinityAPI.DealerHistoryRankType[number]
  historyRanks: TrinityAPI.DealerHistoryRankType
}

function RankRow({ history: { year, rank, revenue }, historyRanks }: RankRowProps) {
  const isCurrent = year === getCurrentYear()
  const previousYearRank = historyRanks[year - 1]?.rank
  const trend = previousYearRank ? rank - previousYearRank : null

  return (
    <Stack direction='row' spacing={2} alignItems='stretch'>
      <Typography
        variant='h6'
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: isCurrent ? 'selected' : 'n100',
          backgroundColor: isCurrent ? 'primary.extraLight' : 'common.white',
        }}
      >
        {String(year).slice(-2)}
      </Typography>

      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        flex={1}
        border={isCurrent ? 'selected' : 'n100'}
        borderRadius={1}
        p={2}
      >
        <Stack spacing={0.5}>
          <Stack direction='row' spacing={1} alignItems='baseline'>
            <Typography variant='h5'>{year}</Typography>
            <Typography variant='smallBody2'>{isCurrent ? 'Current' : 'Final'}</Typography>
          </Stack>
          <Typography variant='smallBody2'>{isCurrent ? 'Current Spend' : 'Final Spend'}</Typography>
          <Typography variant='body3'>{formatMoney('USD', revenue)}</Typography>
        </Stack>

        <Stack spacing={0.5} alignItems='flex-end'>
          <Typography variant='h4'>{`#${rank}`}</Typography>
          <Trend trend={trend} />
        </Stack>
      </Stack>
    </Stack>
  )
}

function Trend({ trend }: { trend: number | null }) {
  if (trend === null) return <Typography variant='smallBody1'>—</Typography>

  const Icon = trend > 0 ? TrendingDown : trend < 0 ? TrendingUp : TrendingFlat
  const symbol = trend > 0 ? '-' : trend < 0 ? '+' : ''
  const color = trend > 0 ? 'error.main' : trend < 0 ? 'success.main' : 'text.secondary'

  return (
    <Stack direction='row' spacing={0.5} alignItems='center' sx={{ color }}>
      <Icon fontSize='small' />
      <Typography color='inherit' variant='smallBody1'>{`${symbol}${Math.abs(trend)}`}</Typography>
    </Stack>
  )
}
